const SINGAPORE_TZ = 'Asia/Singapore';

/**
 * Parse datetime-local / ISO value. Bare `YYYY-MM-DDTHH:mm` is Singapore wall time.
 */
export function parseSingaporeDateTime(value) {
    if (value == null || value === '') return null;
    if (value instanceof Date) {
        if (!Number.isFinite(value.getTime())) return null;
        return value;
    }

    const str = String(value).trim();

    const datetimeLocalMatch = str.match(/^(\d{4})-(\d{2})-(\d{2})T(\d{2}):(\d{2})(?::(\d{2}))?$/);
    if (datetimeLocalMatch) {
        const [, y, mo, d, h, mi, s] = datetimeLocalMatch;
        const utcMs = Date.UTC(+y, +mo - 1, +d, +h - 8, +mi, +(s || 0));
        return new Date(utcMs);
    }

    const dateOnlyMatch = str.match(/^(\d{4})-(\d{2})-(\d{2})$/);
    if (dateOnlyMatch) {
        const [, y, mo, d] = dateOnlyMatch;
        return new Date(Date.UTC(+y, +mo - 1, +d, -8, 0, 0));
    }

    const d = new Date(str);
    if (!Number.isFinite(d.getTime())) return null;
    return d;
}

/** Format for `<input type="date">` in Asia/Singapore. */
export function toSingaporeDateInputValue(value) {
    const parsed = parseSingaporeDateTime(value);
    if (!parsed) return '';
    const parts = new Intl.DateTimeFormat('en-CA', {
        timeZone: SINGAPORE_TZ,
        year: 'numeric',
        month: '2-digit',
        day: '2-digit',
    }).formatToParts(parsed);
    const y = parts.find((p) => p.type === 'year')?.value;
    const m = parts.find((p) => p.type === 'month')?.value;
    const d = parts.find((p) => p.type === 'day')?.value;
    return y && m && d ? `${y}-${m}-${d}` : '';
}

/** Value for `<input type="datetime-local">` — Singapore wall clock. */
export function toSingaporeDatetimeLocalValue(value) {
    const parsed = parseSingaporeDateTime(value);
    if (!parsed) return '';
    const parts = new Intl.DateTimeFormat('en-CA', {
        timeZone: SINGAPORE_TZ,
        year: 'numeric',
        month: '2-digit',
        day: '2-digit',
        hour: '2-digit',
        minute: '2-digit',
        hour12: false,
    }).formatToParts(parsed);
    const get = (type) => parts.find((p) => p.type === type)?.value;
    const y = get('year');
    const m = get('month');
    const d = get('day');
    const h = get('hour');
    const mi = get('minute');
    if (!y || !m || !d || h == null || mi == null) return '';
    return `${y}-${m}-${d}T${h.padStart(2, '0')}:${mi.padStart(2, '0')}`;
}

/** Date in Singapore as dd/mm/yy */
export function formatSingaporeDateDmy(value, fallback = '—') {
    const parsed = parseSingaporeDateTime(value);
    if (!parsed) return fallback;
    const parts = new Intl.DateTimeFormat('en-CA', {
        timeZone: SINGAPORE_TZ,
        year: '2-digit',
        month: '2-digit',
        day: '2-digit',
    }).formatToParts(parsed);
    const get = (type) => parts.find((p) => p.type === type)?.value;
    const d = get('day');
    const m = get('month');
    const y = get('year');
    if (!d || !m || !y) return fallback;
    return `${d}/${m}/${y}`;
}

/** Date in Singapore as dd/mm/yyyy */
export function formatSingaporeDateDmyFull(value, fallback = '—') {
    const parsed = parseSingaporeDateTime(value);
    if (!parsed) return fallback;
    const parts = new Intl.DateTimeFormat('en-CA', {
        timeZone: SINGAPORE_TZ,
        year: 'numeric',
        month: '2-digit',
        day: '2-digit',
    }).formatToParts(parsed);
    const get = (type) => parts.find((p) => p.type === type)?.value;
    const d = get('day');
    const m = get('month');
    const y = get('year');
    if (!d || !m || !y) return fallback;
    return `${d}/${m}/${y}`;
}

/** Short date in Singapore e.g. 23 Jun */
export function formatSingaporeDateShort(value, fallback = '—') {
    const parsed = parseSingaporeDateTime(value);
    if (!parsed) return fallback;
    return new Intl.DateTimeFormat('en-SG', {
        timeZone: SINGAPORE_TZ,
        day: 'numeric',
        month: 'short',
    }).format(parsed);
}

/** Display in Singapore for lists/modals. */
export function formatSingaporeDateTime(value, fallback = '—') {
    const parsed = parseSingaporeDateTime(value);
    if (!parsed) return fallback;
    return new Intl.DateTimeFormat('en-SG', {
        timeZone: SINGAPORE_TZ,
        year: 'numeric',
        month: 'short',
        day: 'numeric',
        hour: '2-digit',
        minute: '2-digit',
        hour12: true,
    }).format(parsed);
}

/** Singapore datetime as `dd/mm/yy h:mm am/pm`. */
export function formatSingaporeDateTimeDmyAmPm(value, fallback = '—') {
    const parsed = parseSingaporeDateTime(value);
    if (!parsed) return fallback;
    const parts = new Intl.DateTimeFormat('en-SG', {
        timeZone: SINGAPORE_TZ,
        year: '2-digit',
        month: '2-digit',
        day: '2-digit',
        hour: 'numeric',
        minute: '2-digit',
        hour12: true,
    }).formatToParts(parsed);
    const get = (type) => parts.find((p) => p.type === type)?.value;
    const d = get('day');
    const m = get('month');
    const y = get('year');
    const h = get('hour');
    const mi = get('minute');
    const dayPeriod = (get('dayPeriod') || '').toLowerCase();
    if (!d || !m || !y || h == null || mi == null) return fallback;
    return `${d}/${m}/${y} ${h}:${mi} ${dayPeriod}`.trim();
}

/** Singapore datetime as `dd/mm/yy hh:mm` (24-hour). */
export function formatSingaporeDateTimeDmyHm(value, fallback = '—') {
    const parsed = parseSingaporeDateTime(value);
    if (!parsed) return fallback;
    const parts = new Intl.DateTimeFormat('en-SG', {
        timeZone: SINGAPORE_TZ,
        year: '2-digit',
        month: '2-digit',
        day: '2-digit',
        hour: '2-digit',
        minute: '2-digit',
        hour12: false,
    }).formatToParts(parsed);
    const get = (type) => parts.find((p) => p.type === type)?.value;
    const d = get('day');
    const m = get('month');
    const y = get('year');
    let h = get('hour');
    const mi = get('minute');
    if (!d || !m || !y || h == null || mi == null) return fallback;
    if (h === '24') h = '00';
    return `${d}/${m}/${y} ${h}:${mi}`;
}

/** Singapore clock only, 24-hour `HH:mm` (e.g. 09:00, 13:00). */
export function formatSingaporeTimeHm(value, fallback = '—') {
    const parsed = parseSingaporeDateTime(value);
    if (!parsed) return fallback;
    const parts = new Intl.DateTimeFormat('en-SG', {
        timeZone: SINGAPORE_TZ,
        hour: '2-digit',
        minute: '2-digit',
        hour12: false,
    }).formatToParts(parsed);
    const get = (type) => parts.find((p) => p.type === type)?.value;
    let h = get('hour');
    const mi = get('minute');
    if (h == null || mi == null) return fallback;
    if (h === '24') h = '00';
    return `${String(h).padStart(2, '0')}:${String(mi).padStart(2, '0')}`;
}

/** Compact SG stamp for AutoBot last-sent, e.g. 230826 11:00am */
export function formatSingaporeCompactStamp(value, fallback = '—') {
    const parsed = parseSingaporeDateTime(value);
    if (!parsed) return fallback;
    const parts = new Intl.DateTimeFormat('en-SG', {
        timeZone: SINGAPORE_TZ,
        year: '2-digit',
        month: '2-digit',
        day: '2-digit',
        hour: 'numeric',
        minute: '2-digit',
        hour12: true,
    }).formatToParts(parsed);
    const get = (type) => parts.find((p) => p.type === type)?.value;
    const d = get('day');
    const m = get('month');
    const y = get('year');
    const h = get('hour');
    const mi = get('minute');
    const dayPeriod = (get('dayPeriod') || '').toLowerCase().replace(/\s/g, '');
    if (!d || !m || !y || h == null || mi == null) return fallback;
    return `${d}${m}${y} ${h}:${mi}${dayPeriod}`;
}

/** True when value is a valid datetime strictly after now (Singapore-parsed). */
export function isFutureSingaporeDateTime(value) {
    const parsed = parseSingaporeDateTime(value);
    if (!parsed) return false;
    return parsed.getTime() > Date.now();
}

/** Current moment as datetime-local string in Singapore. */
export function singaporeNowDatetimeLocal() {
    return toSingaporeDatetimeLocalValue(new Date());
}
